import { useEffect, useRef, useState } from 'react';
import { FirestoreService } from '../firestore-operations';
import type { WithId } from '../types';
import type { QueryOptions, WhereClause, OrderByClause } from '../types/query-options';

/**
 * Hook to fetch a single document from Firestore
 * @param service Instance of FirestoreService
 * @param id Document ID to fetch
 * @returns { data, loading, error }
 */
export function useDocument<T extends object>(
  service: FirestoreService<T>,
  id: string
) {
  const [data, setData] = useState<WithId<T> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const doc = await service.get(id);
        setData(doc);
      } catch (err) {
        console.error('Error fetching document:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [service, id]);

  return { data, loading, error };
}

/**
 * Hook to fetch a collection from Firestore with pagination support
 * @param service Instance of FirestoreService
 * @param options Query options (where, orderBy, limit)
 * @returns { data, loading, error, hasMore, fetchMore, refresh }
 */
export function useCollection<T extends object>(
  service: FirestoreService<T>,
  options: Omit<QueryOptions<T>, 'startAfter' | 'endBefore' | 'last'> = {}
) {
  const [data, setData] = useState<WithId<T>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const lastDocRef = useRef<any>(null);

  const fetchData = async (reset = false) => {
    if ((!hasMore && !reset) || loading) return;
    
    try {
      setLoading(true);
      
      const queryOptions = {
        ...options,
        ...(lastDocRef.current && !reset && { startAfter: lastDocRef.current })
      };
      
      // We need to cast here because of TypeScript limitations with spread operator and generics
      const result = await (service as any).query(queryOptions);
      
      setData(prev => reset ? result : [...prev, ...result]);
      setHasMore(result.length === (options.limit || 20));
      
      if (result.length > 0) {
        lastDocRef.current = result[result.length - 1];
      }
    } catch (err) {
      console.error('Error fetching collection:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  const refresh = () => {
    lastDocRef.current = null;
    return fetchData(true);
  };

  useEffect(() => {
    refresh();
  }, [JSON.stringify(options)]); // Re-run when options change

  return { 
    data, 
    loading, 
    error, 
    hasMore, 
    fetchMore: () => fetchData(false), 
    refresh 
  };
}

/**
 * Hook to subscribe to real-time updates for a collection
 * @param service Instance of FirestoreService
 * @param options Query options (where, orderBy, limit)
 * @returns { data, loading, error }
 */
export function useRealtimeCollection<T extends object>(
  service: FirestoreService<T>,
  options: Omit<QueryOptions<T>, 'startAfter' | 'endBefore' | 'last'> = {}
) {
  const [data, setData] = useState<WithId<T>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const unsubscribeRef = useRef<() => void>();
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    setLoading(true);
    
    try {
      unsubscribeRef.current = service.subscribe(
        (items) => {
          if (isMounted.current) {
            setData(items);
            setLoading(false);
          }
        },
        options
      );
    } catch (err) {
      console.error('Error setting up realtime subscription:', err);
      if (isMounted.current) {
        setError(err as Error);
        setLoading(false);
      }
    }

    // Cleanup subscription on unmount
    return () => {
      isMounted.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [service, JSON.stringify(options)]);

  return { data, loading, error };
}

/**
 * Hook to get a Firestore service instance
 * @param collectionName Name of the Firestore collection
 * @returns Instance of FirestoreService
 */
export function useFirestoreService<T extends object>(collectionName: string) {
  const serviceRef = useRef<FirestoreService<T>>();
  
  if (!serviceRef.current) {
    serviceRef.current = new FirestoreService<T>(collectionName);
  }
  
  return serviceRef.current;
}
